import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useAppSelector, useAppDispatch } from '../store/storeTypes';
import closeIcon from '../img/icons/closeNoRing.svg';
import DietaryIcons from './DietaryIcons';
import {
    getCals,
    setDefaultSubProducts,
    dereffItems,
    useQuickAddProperties
} from '../helpers/basketHelpers';
import store from '../store/store';
import { useConfig } from '../helpers/useConfig';
import { freshRelevance, gtmEvent } from '../helpers/commonHelpers';
import CloseBannerSVG from '../components/promotion/CloseBannerSVG';
import { setShowPromotionBanner } from '../store/sessionSlice';

const PromotionCardItem = props => {
    const isSmallScreen = useSelector(state => state.session.isSmallScreen);
    const customer = useSelector(state => state.session.customer);
    const restaurant = useSelector(state => state.session.restaurant);
    const { idx, bundleItem, addItem, navigateTo } = props;
    const { id, cost, calculatedCost, quantity, subProducts, basePrice, dietPreferences, outOfStock, imageUrl } = props.item;
    const { name, description, desktopImageUrl, mobileImageUrl, bgColor, fontColor, productId, ctaBackgroundColor, ctaFontColor, dietaryColor } = props.settings
    const [selectedBaseCost, setSelectedBaseCost] = useState(cost || basePrice);
    const [selectedBase, setSelectedBase] = useState();
    const baseSelections = useAppSelector(state => state.menu.baseSelections);
    const showCalories = useAppSelector(state => state.menu.showCalories);
    const { showPromotionBanner } = useAppSelector(state => state.session);
    let displayCost = calculatedCost || selectedBaseCost;
    if (!displayCost) displayCost = 0;
    const dietaryOptions = dietPreferences;
    const { isCustomisable, isAddable, isPizza, popupRequired } = useQuickAddProperties(props.item);
    const promoContainerTestId = 'promotion-container-card';
    const promoLeftTestId = 'promotion-card-left';
    const promoRightTestId = 'promotion-card-right';
    const promoCardTitleTestId = 'promotion-card-title';
    const descTestId = 'promotion-card-desc';
    const buttonProps = []
    const config = useConfig();
    const brand = config?.BRAND;
    const encodedName = encodeURIComponent(props.item.name);
    const dispatch = useAppDispatch();

    const handleCustomiseClick = () => {
        if (displayCost > 0) {
            AddToGtmEvent();

            if (bundleItem) {
                navigateTo(`/menu/${restaurant?.id}/bundle/${encodedName}`);
            }
            else {
                navigateTo(`/menu/${restaurant?.id}/customise/${id}`, { menuItem: props.item })
            }
        }
    };

    const AddToGtmEvent = () => {
        const { customer, basket, menu: { activeCategoryName }, session: { inApp } } = store.getState();
        const event = {
            'currency': brand === 'PE' ? 'GBP' : 'EUR',
            clickAndCollectAction: basket.isDelivery ? 'Delivery_Product_Details_Clicked' : 'Collection_Product_Details_Clicked',
            channel: inApp ? 'App' : 'Web',
            value: basket.total,
        };
        if (customer?.customerSessionToken?.pizzaExpressId) {
            event.customerId = customer.customerSessionToken.pizzaExpressId;
        }

        const item = {
            item_name: props.item.name,
            price: props.item.cost ? props.item.cost : props.item.basePrice, // Bundle obj uses basePrice
            quantity: 1,
            item_brand: brand,
            item_category: activeCategoryName
        };
        if (props.item.id) {  // Bundle obj doesn't have id
            item['item_id'] = props.item.id;
        }
        else if (props.item.eposDiscountId) {
            item['item_id'] = props.item.eposDiscountId;
        }
        event['items'] = [item];
        gtmEvent('product_details_event', event);
    };

    if (((isCustomisable && !isAddable) || bundleItem) && productId) {
        buttonProps.push({
            text: 'Customise',
            disabled: outOfStock,
            onClick: () => {
                handleCustomiseClick();
            },
            testId: 'navigate-customise-promotion'
        });
    }

    const [showCard, setShowCard] = useState(showPromotionBanner);
    function decodeHTML(html) {
        const txt = document.createElement('textarea');
        txt.innerHTML = html;
        return txt.value;
    }

    const getCalories = () => {
        if (!showCalories) return null;
        const cals = getCals(props.item, selectedBase, true);
        if (!cals?.length) return null;
        return <span>{cals}</span>;
    };

    const getBaseSelection = () => {
        if (isPizza && props.item.subProducts?.length > 0 && props.item.subProducts[0].defaultSubProduct) {
            return props.item.subProducts[0]?.subProducts.find(b => b.id === props.item.subProducts[0].defaultSubProduct)?.name;
        }
    };

    useEffect(() => {
        if (subProducts && subProducts[0].subProducts.length > 1) {
            const matchingBaseSelection = baseSelections.find(b => b.product === id);
            if (matchingBaseSelection) {
                setSelectedBase(matchingBaseSelection.base);
                setSelectedBaseCost(subProducts[0].subProducts.find(s => s.id === matchingBaseSelection.base).cost);
            } else {
                const defaultSelection = subProducts[0].defaultSubProduct;
                const lastSelection = subProducts[0].subProducts[subProducts[0].subProducts.length - 1].id;
                const selection = subProducts[0].subProducts.find(sp => sp.id === (defaultSelection ?? lastSelection));
                if (selection) {
                    setSelectedBase(selection.id);
                    setSelectedBaseCost(selection.cost);
                }
            }
        }
    }, [subProducts, baseSelections]);

    useEffect(() => {
        if (showCard != showPromotionBanner) dispatch(setShowPromotionBanner(showCard));
    }, [showCard]);

    const handleAddToBasket = () => {
        if (!outOfStock) {
            if (isCustomisable) {
                const choices = [];
                let quickAddItem = dereffItems(props.item);

                if (quickAddItem.subProducts[0].defaultSubProduct && selectedBase) {
                    const selectedDefault = quickAddItem.subProducts[0].subProducts.find(base => base.id === selectedBase);
                    quickAddItem.displayPrice = selectedDefault.cost;
                    choices.push({ id: quickAddItem.subProducts[0].id, defaultId: selectedBase });
                }

                setDefaultSubProducts(quickAddItem, choices);
                freshRelevance('pageChange', null, { 'sendBeacon': true });
                addItem(quickAddItem, true);
            } else {
                addItem(props.item);
            }
        }
    };
    const calories = getCalories();
    const displayBase = getBaseSelection();
    const header = decodeHTML(decodeHTML(name));
    if (!isSmallScreen) {
        return (<div className={`desktop-promotion-container`} data-testid={promoContainerTestId}>
            <div className={`left`} style={{ backgroundColor: `${bgColor}` }} data-testid={promoLeftTestId}>
                <div className="card-title-wrapper">
                    <p className={`card-title is-text-weight-semibold`} style={{ color: `${fontColor}` }} data-testid={promoCardTitleTestId}>
                        {header}
                    </p>
                </div>
                <div className="card-description-wrapper">
                    <p className={`description is-text-weight-semibold`} style={{ color: `${fontColor}` }} data-testid={descTestId}>{description}</p>
                </div>
                <div className="button-wrapper block is-flex">
                    {displayCost > 0 &&
                        <>
                            {buttonProps.length == 1 ?
                                <button
                                    className='prmotion-btn'
                                    disabled={buttonProps[0].disabled} data-testid={buttonProps[0].testId}
                                    style={{ opacity: outOfStock ? '0.2' : '1', color: `${ctaFontColor}`, backgroundColor: `${ctaBackgroundColor}` }}
                                    onClick={() => {
                                        buttonProps[0].onClick();
                                    }}>
                                    {buttonProps[0].text}
                                </button>
                                :
                                <button
                                    className='prmotion-btn'
                                    disabled={outOfStock} data-testid='navigate-add-promotion'
                                    style={{ opacity: outOfStock ? '0.2' : '1', color: `${ctaFontColor}`, backgroundColor: `${ctaBackgroundColor}` }}
                                    onClick={() => {
                                        handleAddToBasket();
                                    }}>
                                    Add to basket
                                </button>
                            }

                            <div className={`product-price`} style={{ color: `${fontColor}` }}>
                                <span
                                    className={`is-text-weight-semibold`}
                                >
                                    {displayCost.toFixed(2)}
                                </span>
                            </div>
                        </>
                    }
                    <div style={{ display: 'flex', width: '100%', flexDirection: 'row', gap: '16px', justifyContent: 'flex-end' }}>
                        {dietaryOptions &&
                            <div className="dietary-option">
                                <DietaryIcons
                                    icons={dietaryOptions}
                                    style={{
                                        marginTop: '0rem',
                                        alignItems: 'center',
                                        justifyContent: 'flex-end',
                                        minWidth: `${dietaryOptions?.length * 90}px`,
                                    }}
                                    iconSize={24}
                                    isPromotion={true}
                                    iconColor={dietaryColor}
                                />
                            </div>
                        }
                        {calories &&
                            <div className={`calories`} style={{ color: `${fontColor}` }}>
                                {calories}
                            </div>
                        }
                    </div>
                </div>
            </div>
            <div className="right" data-testid={promoRightTestId}
                style={{
                    background: `url(${desktopImageUrl}) lightgray 50% / cover no-repeat`
                }}>
            </div>
        </div>
        );
    }
    else {
        return (
            <>
                <div
                className={`basket-card-wrapper is-flex-direction-column is-align-items-stretch is-align-content-stretch overlay ${!showCard ? 'hide-promotion-card' : ''} ${!customer ? 'banner-top-position' : ''}`}
                onClick={() => {
                    if (((isCustomisable && !isAddable) || bundleItem) && productId) {
                        handleCustomiseClick();
                    }
                }}
                data-testid={promoContainerTestId}>
                <div className={`basket-card is-radius-very-large ${isSmallScreen ? 'mobile-promotion-card' : ''}`} style={{ backgroundColor: `${bgColor}` }}>
                    <>
                        <div className='mobile-item-close'>
                            <img src={closeIcon} style={{ opacity: '1' }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowCard(false);
                                }} />
                        </div>
                    </>

                    <div className="basket-card-image-wrapper">
                        <img
                            className='promotion-image'
                            data-testid="promo-image"
                            src={mobileImageUrl}
                            alt="Promotion"
                            style={{
                                objectFit: 'cover',
                                display: 'block',
                            }}
                        />
                    </div>

                    <div className='basket-card-content-layout'>
                        <div className={'basket-card-content-wrapper'}>
                            {/* Title for Mobile - has to be here */}
                            <div className="card-title-wrapper-mobile">
                                <p className={`card-title is-text-weight-semibold promotion-title`} style={{ color: `${fontColor}` }} data-testid={promoCardTitleTestId}>
                                    {header}
                                </p>
                            </div>
                            {/* Description */}
                            <div className={`description-wrapper promotion-description`} style={{ color: `${fontColor}` }}>
                                <p data-testid={descTestId}>
                                    {description}
                                </p>

                            </div>
                            {/* Calories and Dietary Icons */}
                            {!bundleItem && (calories || (dietaryOptions?.length > 0)) && (
                                <div className={`card-calories-base-wrapper-mobile promotion-calories`} style={{ color: `${fontColor}` }}>
                                    {isPizza && displayBase && (
                                        <div className={`card-calories-diet-wrapper-mobile`} style={{ color: `${fontColor}` }}>
                                            <div>{!displayBase ? 'Base' : displayBase}</div>
                                            <DietaryIcons
                                                icons={dietaryOptions}
                                                style={{
                                                    alignItems: 'center',
                                                    marginTop: '0px',
                                                    minWidth: `${dietaryOptions?.length * 16}px`,
                                                }}
                                                iconSize={16}
                                                isPromotion={true}
                                                iconColor={dietaryColor}
                                            />
                                        </div>
                                    )}
                                    <div className={`card-calories-diet-wrapper-mobile`} style={{ color: `${fontColor}` }}>
                                        {!calories ? '' : calories}
                                        {(!isPizza || !displayBase) && (
                                            <DietaryIcons
                                                icons={dietaryOptions}
                                                style={{
                                                    alignItems: 'center',
                                                    marginTop: '0px',
                                                    minWidth: `${dietaryOptions?.length * 16}px`,
                                                }}
                                                iconSize={16}
                                                isPromotion={true}
                                                iconColor={dietaryColor}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                            {displayCost > 0 && productId &&
                                <div className="card-price-wrapper-mobile">
                                    {(
                                        <span
                                            className={`price is-text-weight-semibold promotion-price`} style={{ color: `${fontColor}` }}
                                        >
                                            {displayCost.toFixed(2)}
                                        </span>
                                    )}
                                    {isAddable && !bundleItem &&
                                        <div className="icon is-medium"
                                            style={{ opacity: outOfStock ? '0.2' : '1' }}
                                            disabled={outOfStock}
                                            aria-disabled={outOfStock}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleAddToBasket();
                                            }}
                                        >
                                            <svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg">
                                                <rect x="0.75" y="0.75" width="30.5" height="30.5" rx="15.25" stroke={`${ctaBackgroundColor}`} stroke-width="1.5" fill={`${ctaBackgroundColor}`} />
                                                <path d="M10 16H22M16 22V10" stroke={`${ctaFontColor}`} stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                            </svg>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>

            </div>
                {!showCard &&
                    <div
                        id= 'showPromo'
                        className='show-close-promotion-card'>
                        <CloseBannerSVG color={bgColor} setShowCard={setShowCard}></CloseBannerSVG></div>
                }
            </>
        );
    }
};
export default PromotionCardItem;